import React, { useEffect } from 'react';
import { CloseOutlined, DownOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { intelligenceScrollbar } from 'containers/Intelligence/theme/scrollbar';

import { ORBIT_PAGE_MOCK } from '../orbitPageMock';
import { noop, pg, PlainButton, tint, type } from './pageStyles';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: ${tint('#05090F', 0.6)};
`;

const Drawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  width: 430px;
  max-width: 100vw;
  padding: 20px;
  border-left: 1px solid ${pg.border};
  background: ${pg.rail};
  overflow-y: auto;
  ${intelligenceScrollbar('y')}
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  margin: 0;
  color: ${pg.text};
  ${type('drawerTitle', 700)}
`;

const Subtitle = styled.p`
  margin: 2px 0 0;
  color: ${pg.muted};
  ${type('containerBody')}
`;

const Close = styled(PlainButton)`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 41px;
  height: 38px;
  border: 1px solid ${pg.border};
  border-radius: 9px;
  background: ${pg.card};
  color: ${pg.secondary};
`;

const Field = styled.div`
  & + & {
    margin-top: 16px;
  }
`;

const Label = styled.span`
  display: block;
  margin-bottom: 6px;
  color: ${pg.muted};
  ${type('helping', 600)}
`;

const Select = styled(PlainButton)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 42px;
  padding: 0 12px;
  border: 1px solid ${pg.border};
  border-radius: 9px;
  background: ${pg.card};
  color: ${pg.text};
  ${type('conversation')}
`;

const Buttons = styled.div`
  display: flex;
  margin-top: 20px;

  > * {
    flex: 1 1 0;
    min-width: 0;
  }

  > * + * {
    margin-left: 8px;
  }
`;

const Button = styled(PlainButton)<{ $primary?: boolean }>`
  height: 38px;
  border: 1px solid ${({ $primary }) => ($primary ? pg.primary : pg.border)};
  border-radius: 9px;
  background: ${({ $primary }) => ($primary ? pg.primary : pg.card)};
  color: #ffffff;
  text-align: center;
  ${type('conflictBody', 600)}
`;

const TITLE_ID = 'orbit-page-context-title';

const OrbitContextDrawer: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { drawer } = ORBIT_PAGE_MOCK;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <Backdrop data-testid="orbit-page-context-backdrop" onClick={onClose} />
      <Drawer role="dialog" aria-modal="true" aria-labelledby={TITLE_ID}>
        <Head>
          <div>
            <Title id={TITLE_ID}>{drawer.title}</Title>
            <Subtitle>{drawer.subtitle}</Subtitle>
          </div>
          <Close type="button" aria-label="Close" onClick={onClose}>
            <CloseOutlined />
          </Close>
        </Head>
        {drawer.fields.map(field => (
          <Field key={field.label}>
            <Label>{field.label}</Label>
            <Select type="button" aria-label={`${field.label}: ${field.value}`} onClick={noop}>
              <span>{field.value}</span>
              <DownOutlined aria-hidden="true" />
            </Select>
          </Field>
        ))}
        <Buttons>
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" $primary onClick={onClose}>
            Apply context
          </Button>
        </Buttons>
      </Drawer>
    </>
  );
};

export default OrbitContextDrawer;
